import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { RootState, AppDispatch } from '../../store';
import { fetchTemplates } from '../../store/templatesSlice';
import { fetchCover } from '../../store/coverSlice';
import { fetchPhotos } from '../../store/photosSlice';
import { fetchAnswers } from '../../store/answersSlice';
import { TopNav } from '../../components/TopNav/TopNav';
import {
  coverByValue,
  decodeCover,
  isCustomCover,
  customUrlFromId,
  defaultCover,
} from '../../components/BookCover';
import { BookCover } from '../../components/BookCover/BookCover';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BookPage, PAGE_PRESETS, PagePreset, DEFAULT_PAGE_PRESET } from '../../components/BookPage';
import { PhotoPage, decodePhotoLayout } from '../../components/PhotoPage';
import { templatesApi, userChaptersApi, Question, Chapter, Photo } from '../../api';
import { getBookStrings } from '../../data/bookStrings';
import { CountryPicker } from '../../components/CountryPicker/CountryPicker';
import { Onboarding } from '../Onboarding/Onboarding';
import { TemplatePicker } from '../Onboarding/TemplatePicker';
import leftIcon from '../../assets/left.svg';
import rightIcon from '../../assets/right.svg';
import chapterFrame from '../../assets/chapter-frame.png';
import '../../styles/margin.css';
import './Dashboard.css';
import BookProgress from '../../components/BookProgress/BookProgress';
import ProofReview from './ProofReview';

gsap.registerPlugin(ScrollTrigger);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const safeISO = (d: Date | null | undefined): string => {
  if (!d || isNaN(d.getTime())) return '';
  return d.toISOString();
};
const safeDateStr = (d: Date | null | undefined): string => {
  const iso = safeISO(d);
  return iso ? iso.split('T')[0] : '';
};

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface UserData {
  username: string;
  answerCount: number;
  questionCount: number;
  photoCount: number;
  completedOnboarding?: boolean;
  status?: 'inProcess' | 'done';
}

const makeFormatDeadline = (months: string[]) => (d: Date | null): string => {
  if (!d || isNaN(d.getTime()) || d.getTime() === 0) return '';
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const daysUntil = (d: Date | null): number | null => {
  if (!d || isNaN(d.getTime()) || d.getTime() === 0) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diff = target.getTime() - now.getTime();
  return Math.round(diff / 86400000);
};

export const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const formatDeadline = makeFormatDeadline(
    t('months', { returnObjects: true }) as string[],
  );

  const templates = useSelector((state: RootState) => state.templates.templates);
  const templatesLoading = useSelector((state: RootState) => state.templates.loading);
  const cover = useSelector((state: RootState) => state.cover.value);
  const photos = useSelector((state: RootState) => state.photos.photos);
  const answers = useSelector((state: RootState) => state.answers.answers);

  const [userData, setUserData] = useState<UserData | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [deliveryCity, setDeliveryCity] = useState<string | null>(null);
  const [deliveryLoading, setDeliveryLoading] = useState(true);
  const [bookStatus, setBookStatus] = useState<string | null>(null);
  const [doneSpreadIndex, setDoneSpreadIndex] = useState<number>(0);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState<'finishBook' | 'confirmFinish' | 'noCover' | null>(null);

  const [street, setStreet] = useState<string>('');
  // «Пропустить редактуру»: книга сразу «Готово под печать» (backend services/skipEditing.ts).
  const [skipEditing, setSkipEditing] = useState(false);
  const [country, setCountry] = useState<string | null>(null);
  const [phone, setPhone] = useState<string>('');
  const [originalAddress, setOriginalAddress] = useState<string | null>(null);
  const [finishMinDate, setFinishMinDate] = useState<Date>(new Date());
  const deadlineRef = useRef<HTMLDivElement | null>(null);

  const [pdfUrl, setPdfUrl] = useState<string>();
  // Редактор прислал вёрстку на утверждение — её блок встаёт вместо «верстки».
  const [proofActive, setProofActive] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const [numPages, setNumPages] = useState<number>();
  const [answersReady, setAnswersReady] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [feedFilter, setFeedFilter] = useState<
    'all' | 'layout' | 'answered' | 'unanswered' | 'photos'
  >('all');
  const [onboardingDoneLocal, setOnboardingDoneLocal] = useState<boolean>(false);
  const [feedView, setFeedView] = useState<'pages' | 'questions' | 'preview'>('pages');

  // ============== preview modal state (book-spread viewer) ==============
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewNumPages, setPreviewNumPages] = useState<number>(0);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  // spreadIndex === 0 → (blank, page 1) — first open-book view, page 1 on the right
  // spreadIndex === k → PDF pages (2k, 2k+1) shown left/right
  const [spreadIndex, setSpreadIndex] = useState<number>(0);

  const template = templates[0];
  const templateId = template?.id;
  const bookStrings = getBookStrings(template?.language);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setUserLoading(false);
          return;
        }
        const response = await axios.get(`${API_URL}/auth/summary`, {
          headers: { Authorization: `${token}` }
        });
        setUserData(response.data);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setUserLoading(false);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchDeliveryData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setDeliveryLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/contacts/my`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('No contacts');
        const data = await response.json();
        const { city, country, address, phone: phoneData, status } = data;

        setOriginalAddress(city);
        setStreet(address || '');
        setCountry(country || null);
        setPhone(phoneData || '');
        setBookStatus(status || null);

        if (city) {
          setDeliveryCity(city);
        }
      } catch {
        /* нет данных по контактам — оставляем пустыми */
      } finally {
        setDeliveryLoading(false);
      }
    };
    fetchDeliveryData();
  }, []);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (templateId) {
      dispatch(fetchCover(Number(templateId)));
      dispatch(fetchPhotos(Number(templateId)));
      templatesApi.getQuestions(Number(templateId)).then(({ data }) => setQuestions(data));
      userChaptersApi.list(Number(templateId)).then(({ data }) => setChapters(data));
    }
  }, [templateId, dispatch]);

  const totalQuestions = userData?.questionCount || 0;
  const answeredQuestions = userData?.answerCount || 0;

  const photosArray = Object.values(photos);

  const userName = userData?.username || '';

  const isBookDone = bookStatus === 'done';

  useEffect(() => {
    if (templateId) {
      setAnswersReady(false);
      dispatch(fetchAnswers(Number(templateId))).then(() => {
        setAnswersReady(true);
      });
    }
  }, [templateId, dispatch]);

  const generatePdf = useCallback(async () => {
    if (!isBookDone || !templateId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/pdf/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const blob = await response.blob();
      setPdfUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (err) {
      console.error('failed to fetch book PDF', err);
    }
  }, [isBookDone, templateId]);

  useEffect(() => {
    if (isBookDone && templateId && answersReady) {
      generatePdf();
    }
  }, [isBookDone, templateId, answersReady, generatePdf]);

  // fetch the backend-generated full-book PDF when the user opens the
  // Preview tab (once per templateId / answers change)
  useEffect(() => {
    if (feedView !== 'preview' || !templateId) return;
    let canceled = false;
    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/pdf/${templateId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`preview ${response.status}`);
        const blob = await response.blob();
        if (canceled) return;
        setPreviewPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (err) {
        console.error('book-preview fetch failed', err);
      } finally {
        if (!canceled) setPreviewLoading(false);
      }
    };
    fetchPreview();
    return () => {
      canceled = true;
    };
  }, [feedView, templateId, answersReady]);

  // keyboard nav for the book preview
  useEffect(() => {
    if (feedView !== 'preview') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        setSpreadIndex((i) => {
          const maxSpread = Math.max(
            0,
            Math.ceil((previewNumPages + 1) / 2) - 1,
          );
          return Math.min(i + 1, maxSpread);
        });
      } else if (e.key === 'ArrowLeft') {
        setSpreadIndex((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [feedView, previewNumPages]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupType(null);
  };

  // Обложка обязательна до завершения: без названия книги показываем попап
  // «создайте обложку» и не пускаем к подтверждению.
  const fetchCoverReady = async (): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const res = await fetch(`${API_URL}/covers/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return false;
      const cov = await res.json();
      return !!(cov?.bookTitle && String(cov.bookTitle).trim());
    } catch {
      return false;
    }
  };

  const handleFinishButtonClick = async () => {
    const ready = await fetchCoverReady();
    setPopupType(ready ? 'confirmFinish' : 'noCover');
    setShowPopup(true);
  };

  const handleConfirmFinishProceed = async () => {
    closePopup();
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response2 = await fetch(`${API_URL}/covers/${templateId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response2.ok) {
        setPopupType('noCover');
        setShowPopup(true);
        return;
      }
      const response = await fetch(`${API_URL}/contacts/my`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const { city, country: countryData, address, phone: phoneData } = data;

      setOriginalAddress(city);
      setDeliveryCity(city);
      setStreet(address || '');
      setCountry(countryData || null);
      setPhone(phoneData || '');

      setPopupType('finishBook');
      setShowPopup(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleFinishBook = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const updatedData = {
        country,
        city: deliveryCity || originalAddress,
        address: street || '',
        phone: phone,
        status: 'done',
        skipEditing,
      };
      await fetch(`${API_URL}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData),
      });
      setShowPopup(false);
      setBookStatus('done');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const hasValidCity = !!(deliveryCity || originalAddress);
  const isFinishBookSaveDisabled = !country || !hasValidCity || !street || !phone;

  const coverReady = !!cover?.coverUrl && !!cover?.bookTitle;
  const photoCount = Object.keys(photos).length;

  const coverPaintingId = decodeCover(cover?.coverUrl);
  const coverIsCustom = isCustomCover(coverPaintingId);
  const coverEntry = coverIsCustom ? undefined : coverByValue(coverPaintingId);
  const coverBgUrl = coverIsCustom
    ? customUrlFromId(coverPaintingId)
    : coverEntry?.url ?? '';

  // ============== framer-style pinned scroll runway ==============
  // intro is a tall (200vh) container holding a sticky 100vh inner.
  // while sticky is pinned the user scrolls a "runway" — wheel/touch input
  // keeps the question on screen, scaling up and lifting away as --p climbs
  // 0 → 1 across the runway. only after the runway is consumed does the
  // sticky release and modules come into view. heaviness comes from the
  // length of the runway, not from damping the wheel.
  // GSAP ScrollTrigger: scrub-driven animation + directional snap.
  // - intro-content fades / scales / lifts over the natural intro height
  // - directional snap means: any forward scroll → snap to end (modules at top);
  //   any backward scroll → snap back to intro start.
  // Modules itself just lives in normal flow; snapping past intro reveals it
  // at the top of the viewport.

  const nextUnanswered = answeredQuestions + 1;
  const nothingStarted = answeredQuestions === 0;

  // ============== book feed (grid of page cells) ==============

  interface FeedCell {
    key: string;
    type:
      | 'question-answered'
      | 'question-empty'
      | 'photo'
      | 'static'
      | 'chapter';
    page: number;
    /** How many pages this cell spans. Photos and chapters are always 1; answered questions may span multiple. */
    pageCount: number;
    // question-specific
    questionId?: number;
    questionText?: string;
    answerText?: string;
    answerPreset?: PagePreset;
    // photo-specific
    photoId?: number;
    photoUrl?: string;
    photoDesc?: string;
    photoDate?: string;
    photoPosition?: string;
    photoShowDate?: boolean;
    photoShowDesc?: boolean;
    // static (front matter) specific
    staticKind?: 'title' | 'blank' | 'toc' | 'letter';
    staticLabel?: string;
    // chapter-specific
    chapterId?: number;
    chapterNumber?: number;
    chapterName?: string;
    chapterHidden?: boolean;
  }

  const cells: FeedCell[] = [];
  // Page rules (mirror backend pdfGenerator.ts exactly):
  //   - With chapters: 6 front-matter pages
  //       1 title / 2 blank / 3 TOC / 4 blank / 5 letter / 6 blank
  //   - Without chapters: 3 front-matter pages
  //       1 title / 2 blank / 3 letter — text starts on the left (page 4)
  //       (orig. blank+TOC pages 2-3 are dropped)
  //   - Each chapter inserts a 1-page chapter title BEFORE the first answered
  //     question of that chapter — counted in the page total. Chapters with
  //     no answered questions still appear in the grid as a placeholder
  //     (page=0) so the user sees the structure of the book.
  //   - Answered question takes ans.pageCount pages (fallback 1).
  //   - Each photo attached to a question is one page, right after that question.
  //   - Unanswered questions / chapters with no answered questions get page=0
  //     and do NOT advance the counter.
  const hasChapters = chapters.length > 0;
  const STATIC_CELLS: Array<{ kind: 'title' | 'blank' | 'toc' | 'letter'; label: string }> = hasChapters
    ? [
        { kind: 'title', label: 'титул' },
        { kind: 'blank', label: 'пусто' },
        { kind: 'toc', label: 'оглавление' },
        { kind: 'blank', label: 'пусто' },
        { kind: 'letter', label: 'письмо от издателя' },
        { kind: 'blank', label: 'пусто' },
      ]
    : [
        { kind: 'title', label: 'титул' },
        { kind: 'blank', label: 'пусто' },
        { kind: 'letter', label: 'письмо от издателя' },
      ];
  const FRONT_MATTER_PAGES = STATIC_CELLS.length;
  STATIC_CELLS.forEach((s, idx) => {
    cells.push({
      key: `static-${idx + 1}`,
      type: 'static',
      page: idx + 1,
      pageCount: 1,
      staticKind: s.kind,
      staticLabel: s.label,
    });
  });
  if (questions.length) {
    const photosArr = Object.values(photos);

    // Find chapter index for a given question sortOrder.
    const chapterFor = (sortOrder: number): number => {
      for (let i = 0; i < chapters.length; i += 1) {
        const c = chapters[i];
        if (sortOrder >= c.fromSort && sortOrder <= c.toSort) return i;
      }
      return -1;
    };

    // Pre-pass: для каждой главы — есть ли хоть один отвеченный вопрос?
    // Это решает, появится ли её title-страница в PDF, и соответственно
    // показывать ли ячейку как реальную страницу или плейсхолдер.
    const chapterHasAnswered = new Map<number, boolean>();
    for (const q of questions) {
      const ans = answers[q.id];
      if (!(ans && ans.text && ans.text.trim())) continue;
      const idx = chapterFor(q.sortOrder);
      if (idx !== -1) chapterHasAnswered.set(idx, true);
    }

    let page = FRONT_MATTER_PAGES + 1;
    let lastChapterShown = -1;
    // Sequential count of chapters that actually appear in the book — drives
    // the "Глава N" label so the grid matches the PDF (1, 2, 3, … rather than
    // the source template's gappy chapter indices).
    let displayedChapterCount = 0;
    for (const q of questions) {
      const ans = answers[q.id];
      const answered = !!(ans && ans.text && ans.text.trim());

      if (hasChapters) {
        const idx = chapterFor(q.sortOrder);
        if (idx !== -1 && idx !== lastChapterShown) {
          lastChapterShown = idx;
          const chap = chapters[idx];
          const isHidden = !!chap.isHidden;
          const hasAnswered = chapterHasAnswered.get(idx) ?? false;

          // Главу показываем в гриде только если внутри уже есть хоть один
          // ответ (как было раньше) — ИЛИ если юзер её явно скрыл, чтобы
          // оставить кликабельный плейсхолдер для возврата. Не доходим до
          // главы → её в гриде нет.
          if (hasAnswered || isHidden) {
            const willRender = hasAnswered && !isHidden;

            let chapterPage = 0;
            let chapterNumber = 0;
            if (willRender) {
              displayedChapterCount += 1;
              chapterPage = page;
              chapterNumber = displayedChapterCount;
              page += 1;
            }

            cells.push({
              key: `chapter-${chap.id}`,
              type: 'chapter',
              page: chapterPage,
              pageCount: 1,
              chapterId: chap.id,
              chapterNumber,
              chapterName: chap.name,
              chapterHidden: isHidden,
            });
          }
        }
      }

      const ansPreset = (PAGE_PRESETS.find((p) => p.id === ans?.preset)?.id
        ?? DEFAULT_PAGE_PRESET) as PagePreset;
      const qPageCount = answered ? ans?.pageCount ?? 1 : 1;
      const qPage = answered ? page : 0;
      cells.push({
        key: `q-${q.id}`,
        type: answered ? 'question-answered' : 'question-empty',
        page: qPage,
        pageCount: qPageCount,
        questionId: q.id,
        questionText: q.text,
        answerText: answered ? ans.text : '',
        answerPreset: ansPreset,
      });
      if (answered) page += qPageCount;
      const attached = photosArr.filter((p: Photo) => p.questionTxt === q.text);
      for (const p of attached) {
        const photoPage = answered ? page : 0;
        cells.push({
          key: `p-${p.id}`,
          type: 'photo',
          page: photoPage,
          pageCount: 1,
          photoId: p.id,
          photoUrl: p.url,
          photoDesc: p.description || '',
          // Только дата, проставленная юзером; createdAt (таймстемп загрузки)
          // в превью не подставляем — нет даты, нет подписи.
          photoDate: p.date || undefined,
          photoPosition: p.position,
          photoShowDate: p.showDate !== false,
          photoShowDesc: p.showDescription !== false,
        });
        if (answered) page += 1;
      }
    }
    const orphanedPhotos = photosArr.filter(
      (p: Photo) => !questions.some(q => q.text === p.questionTxt),
    );
    for (const p of orphanedPhotos) {
      cells.push({
        key: `p-${p.id}`,
        type: 'photo',
        page: 0,
        pageCount: 1,
        photoId: p.id,
        photoUrl: p.url,
        photoDesc: p.description || '',
        photoDate: p.date || undefined,
        photoPosition: p.position,
        photoShowDate: p.showDate !== false,
        photoShowDesc: p.showDescription !== false,
      });
    }
  }

  /** Real book length derived from the same cell layout shown in the grid —
   *  sum of (page + pageCount - 1) across cells with a real page number. */
  const pagesFilled = cells.reduce(
    (max, c) => (c.page > 0 ? Math.max(max, c.page + c.pageCount - 1) : max),
    FRONT_MATTER_PAGES,
  );

  /** Minimum filled pages before the book can be finished. */
  const MIN_PAGES_TO_FINISH = 60;

  /** 200 = aspirational full-book length. 150 = "feels whole". */
  const PAGE_TARGET = 200;
  const progressMilestoneKey = (n: number): string => {
    if (n === 0) return 'progress_milestone_start';
    if (n < 30) return 'progress_milestone_begun';
    if (n < 80) return 'progress_milestone_steady';
    if (n < 130) return 'progress_milestone_halfway';
    if (n < 150) return 'progress_milestone_almost';
    if (n < 200) return 'progress_milestone_full';
    return 'progress_milestone_bonus';
  };
  const progressPct = Math.min(100, Math.round((pagesFilled / PAGE_TARGET) * 100));

  const visibleCells = (() => {
    switch (feedFilter) {
      case 'all':
        return cells;
      case 'layout':
        // book layout: everything except unanswered questions
        return cells.filter((c) => c.type !== 'question-empty');
      case 'answered':
        return cells.filter((c) => c.type === 'question-answered');
      case 'unanswered':
        return cells.filter((c) => c.type === 'question-empty');
      case 'photos':
        return cells.filter((c) => c.type === 'photo');
    }
  })();

  const nextUnansweredQ = questions.find(q => {
    const a = answers[q.id];
    return !a || !a.text || !a.text.trim();
  });

  const pluralize = (n: number, one: string, few: string, many: string) => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} ${few}`;
    return `${n} ${many}`;
  };

  const truncate = (s: string, n: number) =>
    s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s;

  const WORDS_PER_PAGE: Record<PagePreset, number> = {
    heading: 95,
    body: 210,
    balanced: 150,
  };

  const firstPageSlice = (text: string, preset: PagePreset): string => {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '';
    return words.slice(0, WORDS_PER_PAGE[preset]).join(' ');
  };

  const formatPhotoDate = (iso: string | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${d.getFullYear()}`;
  };

  // Step 0 of onboarding — pick a recipient + language → assign a template
  // to the user. Only kicks in after we've confirmed the user has zero
  // templates and hasn't completed onboarding yet.
  const needsTemplate =
    !templatesLoading &&
    templates.length === 0 &&
    !userLoading &&
    userData?.completedOnboarding === false;

  const needsOnboarding =
    !needsTemplate &&
    !onboardingDoneLocal &&
    !!templateId &&
    !userLoading &&
    userData?.completedOnboarding === false;

  return (
    <div className="mg-page">
      {needsTemplate && (
        <TemplatePicker
          onAssigned={() => dispatch(fetchTemplates())}
        />
      )}
      {needsOnboarding && templateId && (
        <Onboarding
          templateId={Number(templateId)}
          recipient={template?.recipient}
          onComplete={async () => {
            try {
              const token = localStorage.getItem('token');
              await fetch(`${API_URL}/auth/complete-onboarding`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
              });
            } catch (err) {
              console.error('failed to mark onboarding complete', err);
            }
            setOnboardingDoneLocal(true);
            if (templateId) dispatch(fetchCover(Number(templateId)));
          }}
        />
      )}
      <TopNav
        onLogout={handleLogout}
        title={
          isBookDone
            ? (t('rite_delivered') as string)
            : userName
              ? userName.toLowerCase()
              : (t('rite_in_progress') as string)
        }
      />

      <main className="mg-main mg-dash">


        <section className="mg-dash__modules">
          <button
            className="mg-dash__mod mg-dash__mod--cover"
            onClick={() => !isBookDone && navigate(`/forms/cover/${templateId}`)}
            type="button"
            disabled={isBookDone}
          >
            <span className="mg-dash__mod-eyebrow">{t('mod_cover_eyebrow')}</span>
            <div className="mg-dash__mod-cover-preview">
              <BookCover
                background={coverBgUrl}
                title={cover?.bookTitle ?? ''}
                author={cover?.authorName ?? ''}
                platePreset={cover?.platePreset ?? 'frame'}
                printPlate
              />
            </div>
          </button>

          <button
            className="mg-dash__mod mg-dash__mod--status"
            onClick={() =>
              !isBookDone &&
              navigate(
                nextUnansweredQ
                  ? `/forms/questions/${templateId}?q=${nextUnansweredQ.id}`
                  : `/forms/questions/${templateId}`,
              )
            }
            type="button"
            disabled={isBookDone}
          >
            <span className="mg-dash__mod-eyebrow">{t('mod_status_eyebrow')}</span>
            <div className="mg-dash__mod-status-row">
              <span className="mg-dash__mod-status-big">{pagesFilled}</span>
              <span className="mg-dash__mod-status-unit">{t('pages_written')}</span>
            </div>
            <div
              className="mg-dash__progress-bar"
              role="progressbar"
              aria-valuenow={pagesFilled}
              aria-valuemin={0}
              aria-valuemax={PAGE_TARGET}
            >
              <div
                className="mg-dash__progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </button>

          <button
            className="mg-dash__mod mg-dash__mod--photos"
            onClick={() => {
              if (isBookDone) return;
              setFeedView('pages');
              setFeedFilter('photos');
              // Wait for the grid to render with the new filter, then scroll to it.
              window.setTimeout(() => {
                const grid = document.querySelector('.mg-dash__grid');
                if (grid) {
                  grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 50);
            }}
            type="button"
            disabled={isBookDone}
          >
            <span className="mg-dash__mod-eyebrow">{t('mod_photos_eyebrow')}</span>
            {photoCount > 0 ? (
              <div className="mg-dash__mod-photos-grid">
                {photosArray.slice(0, 4).map((p: Photo) => (
                  <div key={p.id} className="mg-dash__mod-photo-thumb">
                    {p.url && <img src={p.url} alt="" loading="lazy" />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mg-dash__mod-photos-empty">
                <span>{t('mod_photos_empty')}</span>
              </div>
            )}

          </button>

          {/* Виджет даты доставки выпилен — поле больше не используется. */}

          {!isBookDone && (
            <div className="mg-dash__feed-finish">
              <button
                className="mg-dash__finish-btn"
                onClick={handleFinishButtonClick}
                type="button"
                disabled={pagesFilled < MIN_PAGES_TO_FINISH}
                title={pagesFilled < MIN_PAGES_TO_FINISH ? t('finish_locked', { n: MIN_PAGES_TO_FINISH - pagesFilled }) as string : undefined}
              >
                {t('finish_button')}
              </button>
            </div>
          )}
        </section>

        {!isBookDone && (
          <section className="mg-dash__feed">
            <div className="mg-dash__feed-head">
              <div className="mg-dash__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={feedView === 'pages'}
                  className={`mg-dash__tab ${feedView === 'pages' ? 'mg-dash__tab--active' : ''}`}
                  onClick={() => setFeedView('pages')}
                >
                  {t('tab_pages')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={feedView === 'questions'}
                  className={`mg-dash__tab ${feedView === 'questions' ? 'mg-dash__tab--active' : ''}`}
                  onClick={() => setFeedView('questions')}
                >
                  {t('tab_questions')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={feedView === 'preview'}
                  className={`mg-dash__tab ${feedView === 'preview' ? 'mg-dash__tab--active' : ''}`}
                  onClick={() => setFeedView('preview')}
                >
                  {t('tab_preview')}
                </button>
              </div>
              {feedView === 'pages' && (
                <select
                  className="mg-dash__feed-filter"
                  value={feedFilter}
                  onChange={(e) =>
                    setFeedFilter(e.target.value as typeof feedFilter)
                  }
                >
                  <option value="all">{t('filter_all')}</option>
                  <option value="layout">{t('filter_layout')}</option>
                  <option value="answered">{t('filter_answered')}</option>
                  <option value="unanswered">{t('filter_unanswered')}</option>
                  <option value="photos">{t('filter_photos')}</option>
                </select>
              )}
            </div>

            {nextUnansweredQ && (
              <button
                className="mg-dash__composer-suggest"
                onClick={() =>
                  navigate(
                    `/forms/questions/${templateId}?q=${nextUnansweredQ.id}`,
                  )
                }
                type="button"
              >
                <span className="mg-dash__composer-roman">§</span>
                <span className="mg-dash__composer-q-wrap">
                  <span className="mg-dash__composer-meta">
                    {t('composer_next_q', { n: questions.indexOf(nextUnansweredQ) + 1 })}
                  </span>
                  <span className="mg-dash__composer-q">
                    {truncate(nextUnansweredQ.text, 140)}
                  </span>
                </span>
                <span className="mg-dash__composer-arrow">{t('composer_answer')}</span>
              </button>
            )}

            {feedView === 'pages' && (visibleCells.length > 0 ? (
              <div className="mg-dash__grid">
                {visibleCells.map((cell) => {
                  if (cell.type === 'static') {
                    return (
                      <div
                        key={cell.key}
                        className={`mg-dash__cell mg-dash__cell--static mg-dash__cell--static-${cell.staticKind}`}
                      >
                        <div className="mg-dash__cell-stack">
                          <div className="mg-dash__cell-thumb mg-dash__static-thumb">
                            {cell.staticKind === 'title' && (
                              <div className="mg-dash__static-title">
                                {cover?.authorName && (
                                  <div className="mg-dash__static-title-author">
                                    {cover.authorName}
                                  </div>
                                )}
                                {cover?.bookTitle ? (
                                  <div className="mg-dash__static-title-main">
                                    {cover.bookTitle}
                                  </div>
                                ) : (
                                  <div className="mg-dash__static-title-main mg-dash__static-title-main--placeholder">
                                    {bookStrings.titlePlaceholder}
                                  </div>
                                )}
                                {cover?.receiverName && (
                                  <div className="mg-dash__static-title-recipient">
                                    {bookStrings.receiverFor(cover.receiverName)}
                                  </div>
                                )}
                                <div className="mg-dash__static-title-imprint">{bookStrings.imprint} · 2026</div>
                              </div>
                            )}
                            {cell.staticKind === 'toc' && (
                              <div className="mg-dash__static-toc">
                                <div className="mg-dash__static-toc-label">{bookStrings.toc}</div>
                                <div className="mg-dash__static-toc-list">
                                  {cells
                                    .filter((c) => c.type === 'chapter' && !c.chapterHidden)
                                    .map((c) => (
                                      <div key={c.key} className="mg-dash__static-toc-row">
                                        <span className="mg-dash__static-toc-row-name">
                                          {bookStrings.chapterLabel(c.chapterNumber || 0)}: {c.chapterName}
                                        </span>
                                        <span className="mg-dash__static-toc-row-dots" aria-hidden="true" />
                                        <span className="mg-dash__static-toc-row-page">
                                          {c.page > 0 ? c.page : '—'}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                            {cell.staticKind === 'letter' && (
                              <div className="mg-dash__static-letter">
                                <div className="mg-dash__static-letter-stack">
                                  <div className="mg-dash__static-letter-title">{bookStrings.forewordPre}</div>
                                  <div className="mg-dash__static-letter-body">
                                    {bookStrings.forewordParagraphs.join('\n\n')}
                                  </div>
                                  <div className="mg-dash__static-letter-sig">{bookStrings.publisherSignature}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mg-dash__cell-meta">
                          <img
                            className="mg-dash__cell-side"
                            src={cell.page % 2 === 1 ? rightIcon : leftIcon}
                            alt={cell.page % 2 === 1 ? 'правая' : 'левая'}
                          />
                          <span className="mg-dash__cell-type">
                            СТР. {cell.page}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  if (cell.type === 'chapter') {
                    const hidden = !!cell.chapterHidden;
                    return (
                      <button
                        key={cell.key}
                        className={`mg-dash__cell mg-dash__cell--chapter${
                          hidden ? ' mg-dash__cell--chapter--hidden' : ''
                        }`}
                        onClick={() =>
                          cell.chapterId &&
                          navigate(`/forms/chapter/${templateId}/${cell.chapterId}`)
                        }
                        type="button"
                      >
                        <div className="mg-dash__cell-stack">
                          <div className="mg-dash__cell-thumb mg-dash__chapter-thumb">
                            <img
                              className="mg-dash__chapter-frame mg-dash__chapter-frame--top"
                              src={chapterFrame}
                              alt=""
                              aria-hidden="true"
                            />
                            <div className="mg-dash__chapter-title">
                              {cell.chapterNumber
                                ? `${bookStrings.chapterLabel(cell.chapterNumber)}: ${cell.chapterName}`
                                : cell.chapterName}
                            </div>
                            <img
                              className="mg-dash__chapter-frame mg-dash__chapter-frame--bottom"
                              src={chapterFrame}
                              alt=""
                              aria-hidden="true"
                            />
                          </div>
                        </div>
                        <div className="mg-dash__cell-meta">
                          {cell.page > 0 && (
                            <img
                              className="mg-dash__cell-side"
                              src={cell.page % 2 === 1 ? rightIcon : leftIcon}
                              alt={cell.page % 2 === 1 ? 'правая' : 'левая'}
                            />
                          )}
                          <span className="mg-dash__cell-type">
                            {hidden
                              ? t('chapter_cell_hidden')
                              : cell.page > 0
                                ? `СТР. ${cell.page}`
                                : '—'}
                          </span>
                        </div>
                      </button>
                    );
                  }
                  if (cell.type === 'photo') {
                    const layout = decodePhotoLayout(cell.photoPosition);
                    return (
                      <button
                        key={cell.key}
                        className="mg-dash__cell mg-dash__cell--photo"
                        onClick={() => navigate(`/forms/photos/${templateId}/${cell.photoId}`)}
                        type="button"
                      >
                        <div className="mg-dash__cell-stack">
                          <PhotoPage
                            className="mg-dash__cell-thumb"
                            preset={layout.preset}
                            photoUrl={cell.photoUrl || ''}
                            date={formatPhotoDate(cell.photoDate)}
                            datePos={cell.photoShowDate ? layout.date.pos : 'hidden'}
                            dateSize={layout.date.size}
                            description={cell.photoDesc || ''}
                            descPos={cell.photoShowDesc ? layout.desc.pos : 'hidden'}
                            descSize={layout.desc.size}
                            textColor={layout.color}
                            footerTitle={cover?.bookTitle || ''}
                            pageNumber={cell.page}
                          />
                        </div>
                        <div className="mg-dash__cell-meta">
                          {cell.page > 0 && (
                            <img
                              className="mg-dash__cell-side"
                              src={cell.page % 2 === 1 ? rightIcon : leftIcon}
                              alt={cell.page % 2 === 1 ? 'правая' : 'левая'}
                            />
                          )}
                          <span className="mg-dash__cell-type">
                            {cell.page > 0
                              ? cell.pageCount > 1
                                ? `СТР. ${cell.page}-${cell.page + cell.pageCount - 1}`
                                : `СТР. ${cell.page}`
                              : '—'}
                          </span>
                        </div>
                      </button>
                    );
                  }
                  const empty = cell.type === 'question-empty';
                  const cellPreset: PagePreset = empty
                    ? 'heading'
                    : cell.answerPreset ?? 'body';
                  return (
                    <button
                      key={cell.key}
                      className={`mg-dash__cell mg-dash__cell--q ${empty ? 'mg-dash__cell--empty' : ''}`}
                      onClick={() =>
                        navigate(
                          `/forms/questions/${templateId}?q=${cell.questionId}`,
                        )
                      }
                      type="button"
                    >
                      <div className="mg-dash__cell-stack">
                        <BookPage
                          className="mg-dash__cell-thumb bp--flat"
                          preset={cellPreset}
                          question={cell.questionText || ''}
                          answer={firstPageSlice(cell.answerText || '', cellPreset)}
                          onChange={() => {}}
                          footerTitle={cover?.bookTitle || ''}
                          pageNumber={cell.page}
                          readOnly
                        />
                        {empty && (
                          <div className="mg-dash__cell-overlay">
                            <span className="mg-dash__cell-overlay-label">
                              {t('cell_answer_cta')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="mg-dash__cell-meta">
                        {cell.page > 0 && (
                          <img
                            className="mg-dash__cell-side"
                            src={cell.page % 2 === 1 ? rightIcon : leftIcon}
                            alt={cell.page % 2 === 1 ? 'правая' : 'левая'}
                          />
                        )}
                        <span className="mg-dash__cell-type">
                          {cell.page > 0
                            ? cell.pageCount > 1
                              ? `СТР. ${cell.page}-${cell.page + cell.pageCount - 1}`
                              : `СТР. ${cell.page}`
                            : '—'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="mg-dash__feed-empty">
                {t('feed_empty_state')}
              </div>
            ))}

            {feedView === 'questions' && (
              <div className="mg-dash__qdrawer mg-q__drawer mg-q__drawer--open">
                <div className="mg-q__qlist">
                  {questions.length > 0 ? (
                    <ol className="mg-q__qgroup-list">
                      {questions.map((q, idx) => {
                        const ans = answers[q.id];
                        const done = !!(ans && ans.text && ans.text.trim());
                        return (
                          <li
                            key={q.id}
                            className={`mg-q__qitem ${done ? 'mg-q__qitem--done' : ''}`}
                            onClick={() =>
                              navigate(`/forms/questions/${templateId}?q=${q.id}`)
                            }
                          >
                            <span className="mg-q__qitem-num">
                              {String(idx + 1).padStart(2, '0')}
                            </span>
                            <span className="mg-q__qitem-text">{q.text}</span>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <div className="mg-dash__feed-empty">
                      {t('feed_empty_state')}
                    </div>
                  )}
                </div>
              </div>
            )}

            {feedView === 'preview' && (() => {
              // spread layout: spreadIndex 0 → (blank, p1), index k → (p2k, p2k+1).
              const leftPage = spreadIndex * 2;
              const rightPage = spreadIndex * 2 + 1;
              const maxSpread = Math.max(0, Math.ceil((previewNumPages + 1) / 2) - 1);
              const leftVisible = leftPage >= 1 && leftPage <= previewNumPages;
              const rightVisible = rightPage >= 1 && rightPage <= previewNumPages;
              const onPrev = () => spreadIndex > 0 && setSpreadIndex(spreadIndex - 1);
              const onNext = () => spreadIndex < maxSpread && setSpreadIndex(spreadIndex + 1);
              const indicator =
                leftVisible && rightVisible
                  ? `${leftPage}–${rightPage}`
                  : rightVisible
                    ? `${rightPage}`
                    : leftVisible
                      ? `${leftPage}`
                      : '—';
              return (
                <div className="mg-dash__preview">
                  {previewPdfUrl ? (
                    <Document
                      file={previewPdfUrl}
                      onLoadSuccess={({ numPages }) => setPreviewNumPages(numPages)}
                      loading={
                        <div className="mg-dash__preview-loading mg-meta">
                          {t('pdf_loading')}
                        </div>
                      }
                      className="mg-dash__preview-doc"
                    >
                      <div className="mg-dash__preview-spread">
                        <div className="mg-dash__preview-page">
                          {leftVisible ? (
                            <Page
                              key={`l-${leftPage}`}
                              pageNumber={leftPage}
                              width={380}
                              renderAnnotationLayer={false}
                              renderTextLayer={false}
                              loading={<div className="mg-dash__preview-blank" />}
                            />
                          ) : (
                            <div className="mg-dash__preview-blank" />
                          )}
                        </div>
                        <div className="mg-dash__preview-page">
                          {rightVisible ? (
                            <Page
                              key={`r-${rightPage}`}
                              pageNumber={rightPage}
                              width={380}
                              renderAnnotationLayer={false}
                              renderTextLayer={false}
                              loading={<div className="mg-dash__preview-blank" />}
                            />
                          ) : (
                            <div className="mg-dash__preview-blank" />
                          )}
                        </div>
                      </div>
                    </Document>
                  ) : (
                    <div className="mg-dash__preview-loading mg-meta">
                      {previewLoading ? t('pdf_loading') : '—'}
                    </div>
                  )}

                  <div className="mg-dash__preview-controls">
                    <button
                      type="button"
                      className="mg-dash__preview-nav"
                      onClick={onPrev}
                      disabled={spreadIndex === 0}
                      aria-label={t('preview_prev_aria') as string}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M15 6l-6 6 6 6" />
                      </svg>
                    </button>
                    <span className="mg-dash__preview-indicator">
                      {indicator}
                      {previewNumPages > 0 && (
                        <span className="mg-dash__preview-indicator-total"> / {previewNumPages}</span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="mg-dash__preview-nav"
                      onClick={onNext}
                      disabled={spreadIndex >= maxSpread}
                      aria-label={t('preview_next_aria') as string}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })()}

          </section>
        )}

        {isBookDone && <BookProgress key={progressKey} />}

        <ProofReview onActive={setProofActive} onApproved={() => setProgressKey((k) => k + 1)} />

        {isBookDone && pdfUrl && !proofActive && (() => {
          const total = numPages ?? 0;
          const leftPage = doneSpreadIndex * 2;
          const rightPage = doneSpreadIndex * 2 + 1;
          const maxSpread = Math.max(0, Math.ceil((total + 1) / 2) - 1);
          const leftVisible = leftPage >= 1 && leftPage <= total;
          const rightVisible = rightPage >= 1 && rightPage <= total;
          const onPrev = () => doneSpreadIndex > 0 && setDoneSpreadIndex(doneSpreadIndex - 1);
          const onNext = () => doneSpreadIndex < maxSpread && setDoneSpreadIndex(doneSpreadIndex + 1);
          const indicator =
            leftVisible && rightVisible
              ? `${leftPage}–${rightPage}`
              : rightVisible
                ? `${rightPage}`
                : leftVisible
                  ? `${leftPage}`
                  : '—';
          return (
            <section className="mg-section mg-dash__pages-section">
              <div className="mg-label">{t('layout_label')}</div>
              <div className="mg-dash__preview">
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  loading={
                    <div className="mg-dash__preview-loading mg-meta">
                      {t('pdf_loading')}
                    </div>
                  }
                  className="mg-dash__preview-doc"
                >
                  <div className="mg-dash__preview-spread">
                    <div className="mg-dash__preview-page">
                      {leftVisible ? (
                        <Page
                          key={`done-l-${leftPage}`}
                          pageNumber={leftPage}
                          width={380}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading={<div className="mg-dash__preview-blank" />}
                        />
                      ) : (
                        <div className="mg-dash__preview-blank" />
                      )}
                    </div>
                    <div className="mg-dash__preview-page">
                      {rightVisible ? (
                        <Page
                          key={`done-r-${rightPage}`}
                          pageNumber={rightPage}
                          width={380}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading={<div className="mg-dash__preview-blank" />}
                        />
                      ) : (
                        <div className="mg-dash__preview-blank" />
                      )}
                    </div>
                  </div>
                </Document>

                <div className="mg-dash__preview-controls">
                  <button
                    type="button"
                    className="mg-dash__preview-nav"
                    onClick={onPrev}
                    disabled={doneSpreadIndex === 0}
                    aria-label={t('preview_prev_aria') as string}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M15 6l-6 6 6 6" />
                    </svg>
                  </button>
                  <span className="mg-dash__preview-indicator">
                    {indicator}
                    {total > 0 && (
                      <span className="mg-dash__preview-indicator-total"> / {total}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="mg-dash__preview-nav"
                    onClick={onNext}
                    disabled={doneSpreadIndex >= maxSpread}
                    aria-label={t('preview_next_aria') as string}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                </div>
              </div>
            </section>
          );
        })()}

      </main>

      {showPopup && popupType === 'noCover' && (
        <div className="mg-popup-overlay" onClick={closePopup}>
          <div className="mg-popup mg-popup--rite" onClick={e => e.stopPropagation()}>
            
            <div className="mg-popup__title">{t('no_cover_title')}</div>
            <p className="mg-popup__text">{t('no_cover_text')}</p>
            <div className="mg-popup__actions">
              <button
                className="mg-btn mg-btn--accent"
                onClick={() => navigate(`/forms/cover/${templateId}`)}
              >
                {t('no_cover_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && popupType === 'confirmFinish' && (
        <div className="mg-popup-overlay" onClick={closePopup}>
          <div className="mg-popup mg-popup--rite" onClick={e => e.stopPropagation()}>
            <div className="mg-rite">
              <span>{t('finish_rite_chapter')}</span>
              <span className="mg-rite__sep">·</span>
              <span className="mg-rite__now">{t('finish_rite_now')}</span>
            </div>
            <div className="mg-popup__title">{t('finish_title')}</div>
            <p className="mg-popup__text">{t('finish_text')}</p>
            <div className="mg-popup__actions">
              <button className="mg-btn mg-btn--accent" onClick={handleConfirmFinishProceed}>
                {t('finish_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && popupType === 'finishBook' && (
        <div className="mg-popup-overlay" onClick={closePopup}>
          <div className="mg-popup mg-popup--rite" onClick={e => e.stopPropagation()}>
            <div className="mg-rite">
              <span>{t('finish_rite_chapter')}</span>
              <span className="mg-rite__sep">·</span>
              <span className="mg-rite__now">{t('send_rite_now')}</span>
            </div>
            <div className="mg-popup__title">
              {!originalAddress ? t('send_title_fill') : t('send_title_send')}
            </div>
            <div className="mg-field">
              <label className="mg-field__label">{t('send_field_country')}</label>
              <CountryPicker
                value={country}
                onChange={setCountry}
                placeholder={t('send_country_placeholder') as string}
              />
            </div>
            <div className="mg-field">
              <label className="mg-field__label">{t('send_field_city')}</label>
              <input
                type="text"
                className="mg-input"
                value={deliveryCity || ''}
                onChange={e => setDeliveryCity(e.target.value)}
              />
            </div>
            <div className="mg-field">
              <label className="mg-field__label">{t('send_field_street')}</label>
              <input
                type="text"
                className="mg-input"
                value={street}
                onChange={e => setStreet(e.target.value)}
              />
            </div>
            <div className="mg-field">
              <label className="mg-field__label">{t('send_field_phone')}</label>
              <input
                type="text"
                className="mg-input"
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
            </div>
            <label className="mg-checkbox mg-skip-editing">
              <input
                type="checkbox"
                checked={skipEditing}
                onChange={e => setSkipEditing(e.target.checked)}
              />
              <span>{t('send_skip_editing')}</span>
            </label>
            <div className="mg-popup__actions">
              <button
                className="mg-btn mg-btn--accent"
                disabled={isFinishBookSaveDisabled}
                onClick={handleFinishBook}
              >
                {t('send_submit')}
              </button>
              <button className="mg-btn mg-btn--ghost" onClick={closePopup}>
                {t('send_cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
